import { Badge, Card, Flex, Progress, Text } from "@radix-ui/themes";
import { Zap } from "lucide-react";
import { ManagerEventAutoApproval } from "@/types/manager_events";

interface Props {
  autoApproval: ManagerEventAutoApproval;
}

/**
 * How much room this event has left for media that approve themselves.
 *
 * Renders nothing when the feature is off here *and* off at the venue — a
 * permanent "Auto-approval: off" on every event at every track that never
 * switched it on is noise. An event that deliberately overrides the venue to
 * off still shows, because that is a decision somebody made.
 */
export default function AutoApprovalCard({ autoApproval }: Props) {
  const {
    enabled,
    limit,
    slots_remaining,
    auto_approved_count,
    overridden,
    inherited_enabled,
    inherited_limit,
    credential_gated,
  } = autoApproval;

  if (!enabled && !inherited_enabled && !overridden) return null;

  return (
    <Card>
      <Flex direction="column" gap="2">
        <Flex align="center" justify="between" gap="2">
          <Flex align="center" gap="1">
            <Zap size={14} />
            <Text size="2" weight="medium">
              Media auto-approval
            </Text>
          </Flex>
          <Badge variant="soft" color={enabled ? "green" : "gray"}>
            {enabled ? "On" : "Off"}
          </Badge>
        </Flex>

        {enabled && (
          <>
            <Flex align="baseline" gap="2">
              <Text size="6" weight="bold">
                {slots_remaining}
              </Text>
              <Text size="2" color="gray">
                {slots_remaining === 1 ? "slot" : "slots"} left of {limit}
              </Text>
            </Flex>
            {/* The cap counts auto-approvals only — a manager approving by hand
                past the limit is deliberate and doesn't move this bar. */}
            <Progress
              value={limit > 0 ? (auto_approved_count / limit) * 100 : 0}
            />
            <Text size="1" color="gray">
              {auto_approved_count} auto-approved
              {credential_gated
                ? " — credentialed media only"
                : " — this venue publishes no credential forms, so all media qualify"}
            </Text>
          </>
        )}

        <Text size="1" color="gray">
          {overridden
            ? `This event overrides the venue (${
                inherited_enabled ? `on, limit ${inherited_limit}` : "off"
              })`
            : "Inherited from the venue"}
        </Text>
      </Flex>
    </Card>
  );
}
